// Libraries
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';
// Data
import { SimulateParameters } from '@schemas/services';
// Utils
import { CiiSimulatorSimulationData, OptimizedSpeedsAsNumber, SimulatorRunner } from '@helpers/index';
import { CacheKeys } from '@utils/index';
import { formatDataToFormikState, formatOptimizedSpeeds, optimizedSpeedBaseAsString } from './helpers';
// Types
import { UseCiiSimulatorResponse } from './useCiiSimulator.types';
import { apiGatewayAPI } from '@/src/services';

/**
 * The formData object cant have undefined values to avoid problematic behavior on the form
 */
const defaultParameterRanges = {
  averageBeaufortScale: {
    maxValue: 1,
    minValue: 0,
  },
  averageSpeed: {
    maxValue: 1,
    minValue: 0,
  },
  carbonCredit: {
    maxValue: 1,
    minValue: 0,
  },
  daysSailingPerMonth: {
    maxValue: 1,
    minValue: 0,
  },
};

/**
 * `useCiiSimulator` is a custom hook that orchestrates the CII (Carbon Intensity Indicator) simulator's operations.
 * It manages the start of the simulator, running the simulation, and retrieving optimized speeds.
 *
 * The hook handles loading states and data retrieval for the CII simulator and uses caching to minimize redundant fetch operations.
 *
 * @hook
 * @returns {UseCiiSimulatorResponse}
 * @returns {boolean} isLoading - Indicates if the simulator's start data is being fetched.
 * @returns {boolean} isSimulating - Indicates if a simulation operation is ongoing.
 * @returns {boolean} isOptimizing - Indicates if the optimization operation is ongoing.
 * @returns {object} formData - Contains static data needed for the simulation form like fuel options and parameter ranges.
 * @returns {boolean | undefined} uwc - Represents the status of the Under Water Cleaning feature.
 * @returns {object | undefined} emissionsImpact - Data detailing the impact of emissions.
 * @returns {object | undefined} ratingsPerDistance - Data detailing ratings per distance.
 * @returns {object} graphData - Contains data needed for plotting graphs related to the simulation.
 * @returns {(params: SimulateParameters) => void} runSimulation - Initiates a simulation operation with provided parameters.
 * @returns {(params: SimulateParameters) => void} updateOptimizedSpeeds - Fetches optimized speeds based on provided parameters.
 */
export const useCiiSimulator = (imo?: string): UseCiiSimulatorResponse => {
  const sessionData = useSession();
  const queryClient = useQueryClient();
  const customerId = sessionData.data?.customer.id;
  const simulatorRunner = customerId ? SimulatorRunner.getInstance(Number(customerId), apiGatewayAPI) : undefined;
  const [simulationData, setSimulationData] = useState<CiiSimulatorSimulationData>();
  const [optimizedSpeeds, setOptimizedSpeeds] = useState<OptimizedSpeedsAsNumber>();

  useEffect(() => {
    setSimulationData(undefined);
    setOptimizedSpeeds(undefined);
  }, [imo]);

  /**
   * UseQuery hook to start the simulator and fetch the initial data.
   *
   * @remarks
   * - This hook utilizes the `useQuery` function from `react-query` to handle the fetching and caching logic for starting the simulator.
   * - The key for caching is derived from the `CacheKeys.CII_SIMULATOR` constant and the provided `customerId` and `imo` values.
   * - The simulator is started by calling the `startSimulator` function from the `simulatorRunner` with the given `imo` number.
   * - The query does not refetch when the window is focused, as determined by `refetchOnWindowFocus: false`.
   * - This query is only enabled if `customerId`, `imo`, and `simulatorRunner` are defined and truthy.
   *
   * @returns An object containing:
   * @returns {CiiSimulatorStartData} startData - The starting data fetched from the simulator.
   * @returns {Function} isLoading - A boolean indicating if the fetching of the starting data is currently ongoing.
   */
  const { data: startData, isLoading } = useQuery({
    queryKey: [CacheKeys.CII_SIMULATOR, { customerId, imo }],
    queryFn: async () => await simulatorRunner?.startSimulator(Number(imo)),
    refetchOnWindowFocus: false,
    useErrorBoundary: true,
    enabled: !!customerId && !!imo && !!simulatorRunner,
  });

  /**
   * UseMutation hook to run simulations and manage its results.
   *
   * @remarks
   * - This hook uses the `useMutation` function from `react-query` to handle the mutation state and fetch logic for the simulation.
   * - It checks if the simulation data for the given state already exists in the cache. If it does, it returns the cached data.
   * - If no cached data is found, it runs a new simulation using the `simulatorRunner`.
   * - On a successful mutation, the resulting data is cached using `queryClient`.
   *
   * @returns An object containing:
   * @returns {Function} runSimulation - A function to run the simulation.
   * @returns {boolean} isLoading - A boolean indicating if the simulation is currently running.
   */
  const { mutate: runSimulation, isLoading: isSimulating } = useMutation<
    CiiSimulatorSimulationData | undefined,
    unknown,
    SimulateParameters
  >(
    async (state: SimulateParameters) => {
      const cache = queryClient.getQueryData<CiiSimulatorSimulationData>([`simulate`, { customerId, imo, state }]);
      if (!!cache) {
        return cache;
      }

      return await simulatorRunner?.simulate(state);
    },
    {
      useErrorBoundary: true,
      onSuccess: (simulationData, state) => {
        queryClient.setQueryData([`simulate`, { customerId, imo, state }], simulationData);
        setSimulationData(simulationData);
      },
    }
  );

  /**
   * UseMutation hook to fetch optimized speeds and manage its results.
   *
   * @remarks
   * - This hook utilizes the `useMutation` function from `react-query` to handle the mutation state and fetch logic for fetching optimized speeds.
   * - It initially checks if the optimized speeds for the given state already exist in the cache. If cached data exists, it returns that data.
   * - If no cached data is present, it fetches the optimized speeds using the `simulatorRunner`.
   * - On a successful mutation, the resulting data is cached using `queryClient`.
   *
   * @returns An object containing:
   * @returns {Function} updateOptimizedSpeeds - A function to fetch the optimized speeds.
   * @returns {boolean} isOptimizing - A boolean indicating if the fetching of optimized speeds is currently ongoing.
   */
  const { mutate: updateOptimizedSpeeds, isLoading: isOptimizing } = useMutation<
    OptimizedSpeedsAsNumber | undefined,
    unknown,
    SimulateParameters
  >(
    async (state: SimulateParameters) => {
      const cache = queryClient.getQueryData<OptimizedSpeedsAsNumber>([
        `optimizedSpeeds`,
        { customerId, imo, ...state, ballastSpeedKts: 0, ladenSpeedKts: 0 },
      ]);
      if (!!cache) {
        return cache;
      }

      return await simulatorRunner?.getOptimizedSpeeds(state);
    },
    {
      onSuccess: (speeds, state) => {
        queryClient.setQueryData(
          [`optimizedSpeeds`, { customerId, imo, ...state, ballastSpeedKts: 0, ladenSpeedKts: 0 }],
          speeds
        );
        setOptimizedSpeeds(speeds);
      },
    }
  );

  const formattedOptimizedSpeeds = useMemo(
    () => formatOptimizedSpeeds(optimizedSpeeds) ?? formatOptimizedSpeeds(startData?.optimizedSpeeds),
    [optimizedSpeeds, startData?.optimizedSpeeds]
  );

  return {
    // Flags
    isLoading,
    isSimulating,
    isOptimizing,

    // Data
    formData: {
      // TODO: Replace for a new column in DB with show flag. SPECIFIC REQUEST by product to hide the B100 fuel type.
      fuelOptions: startData?.fuelOptions.filter((option) => option.value != 34) ?? [],
      optimizedSpeeds: formattedOptimizedSpeeds ?? optimizedSpeedBaseAsString,
      defaultParametersValues: formatDataToFormikState(startData?.defaultParametersValues),
      parametersRanges: startData?.parametersRanges ?? defaultParameterRanges,
    },
    uwc: startData?.uwc,
    bulkQuantityUsedMT: simulationData?.bulkQuantityUsedMT,
    bulkFuelIsGreater: simulationData?.bulkFuelIsGreater,
    emissionsImpact: simulationData?.emissionsImpact ?? startData?.emissionsImpact,
    ratingsPerDistance: startData?.ratingsPerDistance,
    graphData: {
      graphicsData: simulationData?.graphicsData ?? startData?.graphicsData,
      ciiRatingBoundaries: startData?.ciiRatingBoundaries,
      operationalYear: startData?.operationalYear,
      currentRating: startData?.currentRating,
      forecastRating: simulationData?.forecastRating ?? startData?.forecastRating,
      requiredCiiScore: startData?.requiredCiiScore,
      lastNoonReportDate: startData?.lastNoonReportDate,
      bulkUsageEndIndex: simulationData?.graphicsData.bulkUsageEndIndex,
    },

    // Functions
    runSimulation,
    updateOptimizedSpeeds,
  };
};
